import { createTheme, MantineThemeOverride } from '@mantine/core';

// Theme colors using CSS variables generated by style-dictionary from Figma.
//
// Every named scale is pointed at its own `--{name}-{name}-N` primitive
// instead of Mantine's `--mantine-color-{name}-N` namespace. Mantine's
// runtime writes its OWN values into `--mantine-color-*` / `--mantine-primary-color-*`
// (derived from its built-in stock palette + `primaryColor`) whenever a scale
// isn't explicitly overridden here, silently clobbering the design-token
// pipeline's actual brand colors with Mantine's generic defaults. Referencing
// the primitives directly sidesteps that collision entirely.
// Exported so a test can assert this list stays in sync with whatever color
// families the token pipeline actually generates (see ui.theme.spec.ts) —
// this list is otherwise just a static, hand-maintained array, and if the
// pipeline adds a new color family without this list being updated, that new
// color silently falls back to Mantine's stock palette the exact same way
// the whole rest of this file exists to prevent.
export const colorNames = [
  'blue',
  'gray',
  'dark',
  'red',
  'green',
  'yellow',
  'teal',
  'orange',
  'violet',
  'grape',
  'cyan',
  'indigo',
  'pink',
  'lime',
  'azure',
] as const;

const colorScale = (name: string) =>
  Array.from(
    { length: 10 },
    (_, shade) => `var(--${name}-${name}-${shade})`
  ) as unknown as readonly [
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string,
    string
  ];

const themeColors = {
  ...Object.fromEntries(colorNames.map((name) => [name, colorScale(name)])),
  // 'primary' aliases to the brand primary (currently blue) via the
  // pipeline's own primary-primary-N var, not Mantine's primaryColor
  // machinery, so it can't be re-clobbered the same way.
  primary: colorScale('primary'),
} as const;

export const uiTheme: MantineThemeOverride = {
  // Use CSS variables for colors
  colors: themeColors,
  // Cowbell's brand primary action color is blue (#1B6FBA via --blue-blue-N).
  primaryColor: 'blue',
};

// Export theme creator function
export function createUiTheme(overrides?: MantineThemeOverride) {
  return createTheme({
    ...uiTheme,
    ...overrides,
  });
}
